import type { SkillData } from '../../spacetime/modules/SkillModule';
import { AuthoritativeQueue } from './AuthoritativeQueue';
import { EntityStore } from './EntityStore';
import { WorldState } from './WorldState';
import type { GridGeometry } from '../../grid';
import { calculateTargetAngleFromTileCenters } from '../../utils/rotation';
import type { StatusEffectSnapshot } from '../../spacetime/types';
import type {
  MapDefinition,
  PortalData,
  QueueFrontState,
  QueuedIntent,
  RemotePlayer,
  RuntimeMob,
  StateChangeDetail,
  StateChangeEvent,
  Npc,
  NpcRuntimeData,
  ChestInventoryRow,
  TileData,
  TilePosition,
  TerrainType,
  BiomeType,
  VisualAssetPlacement,
} from './types';

export type {
  MapDefinition,
  PortalData,
  QueueFrontState,
  QueuedIntent,
  RemotePlayer,
  RuntimeMob,
  StateChangeDetail,
  StateChangeEvent,
  Npc,
  NpcRuntimeData,
  ChestInventoryRow,
  TerrainType,
  BiomeType,
  TileData,
  TilePosition,
  VisualAssetPlacement,
} from './types';
export type { StatusEffectSnapshot } from '../../spacetime/types';

export class GameState extends EventTarget {
  private readonly worldState: WorldState;
  private readonly entities: EntityStore;
  private readonly inputQueue: AuthoritativeQueue;

  private tickCounter = 0;
  private nextTickTarget = 1;
  private selfEntityId: string | null = null;
  private isTransitioning = false;
  private pendingAuthoritativePosition: TilePosition | null = null;
  private pendingAuthoritativeRotation: number | null = null;
  private knownSkills: SkillData[] = [];
  private activeStatusEffects: StatusEffectSnapshot[] = [];
  private viewRadius = 5;
  private selfAppearanceEntityType = 'player';
  private selfAppearanceColor = '#3b82f6';
  private selfAppearanceAccessoriesJson = '{}';

  constructor(map: MapDefinition, initialPosition: TilePosition = { x: 1, y: 1 }, mapId: string = 'default') {
    super();
    this.worldState = new WorldState(map, mapId);
    this.entities = new EntityStore();
    this.inputQueue = new AuthoritativeQueue(initialPosition);
    this.inputQueue.setGrid(this.worldState.getGrid());
  }

  public getCurrentMapId(): string {
    return this.worldState.getCurrentMapId();
  }

  public getGrid(): GridGeometry {
    return this.worldState.getGrid();
  }

  public getIsTransitioning(): boolean {
    return this.isTransitioning;
  }

  public setMap(map: MapDefinition, mapId: string): void {
    this.worldState.setMap(map, mapId);
    this.inputQueue.setGrid(this.worldState.getGrid());
  }

  public getMap(): MapDefinition {
    return this.worldState.getMap();
  }

  public getCharacterPosition(): TilePosition {
    return this.inputQueue.getCharacterPosition();
  }

  public getCharacterRotation(): number {
    return this.inputQueue.getCharacterRotation();
  }

  public setSelfEntityId(entityId: string): void {
    this.selfEntityId = entityId || null;
  }

  public getSelfEntityId(): string | null {
    return this.selfEntityId;
  }

  public setSelfAppearance(entityType: string, color: string, accessoriesJson: string): void {
    this.selfAppearanceEntityType = entityType || 'player';
    this.selfAppearanceColor = color || '#3b82f6';
    this.selfAppearanceAccessoriesJson = accessoriesJson || '{}';
  }

  public getSelfAppearance(): { entityType: string; color: string; accessoriesJson: string } {
    return {
      entityType: this.selfAppearanceEntityType,
      color: this.selfAppearanceColor,
      accessoriesJson: this.selfAppearanceAccessoriesJson,
    };
  }

  public setKnownSkills(skills: SkillData[]): void {
    this.knownSkills = skills;
  }

  public getKnownSkills(): SkillData[] {
    return this.knownSkills;
  }

  public setStatusEffects(effects: StatusEffectSnapshot[]): void {
    this.activeStatusEffects = effects;
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'statusEffectsUpdated', effects },
      })
    );
  }

  public getActiveStatusEffects(): StatusEffectSnapshot[] {
    return this.activeStatusEffects;
  }

  public setMoveDistance(distance: number): void {
    this.inputQueue.setMoveDistance(distance);
  }

  public getMoveDistance(): number {
    return this.inputQueue.getMoveDistance();
  }

  public setViewRadius(radius: number): void {
    if (!Number.isFinite(radius)) {
      return;
    }
    this.viewRadius = Math.max(0, Math.floor(radius));
  }

  public getViewRadius(): number {
    if (this.worldState.getMap().visibilityMode === 'full_map') {
      const map = this.worldState.getMap();
      return Math.max(0, map.width + map.height);
    }
    return this.viewRadius;
  }

  public getQueueFrontRotation(): number {
    return this.inputQueue.getQueueFrontRotation();
  }

  public getQueueFrontPosition(): QueueFrontState {
    return this.inputQueue.getQueueFrontPosition();
  }

  public enqueueTurn(direction: number, intentId?: string): void {
    this.inputQueue.enqueueTurn(direction, intentId);
    this.emitQueueUpdate();
  }

  public getPortals(): ReadonlyArray<PortalData> {
    return this.worldState.getPortals();
  }

  public getPortalAt(x: number, y: number): PortalData | null {
    return this.worldState.getPortalAt(x, y);
  }

  public startPortalTransition(destMapId: string): void {
    this.isTransitioning = true;
    this.inputQueue.clearServerQueue();
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'portalTransitionStart', destMapId },
      })
    );
  }

  public completePortalTransition(newMap: MapDefinition, newMapId: string, newPosition: TilePosition): void {
    this.worldState.setMap(newMap, newMapId);
    this.inputQueue.setCharacterPosition(newPosition);
    this.isTransitioning = false;
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'portalTransitionComplete', mapId: newMapId, position: newPosition },
      })
    );
  }

  public setTransitioning(value: boolean): void {
    if (this.isTransitioning && !value) {
      this.dispatchEvent(
        new CustomEvent<StateChangeDetail>('stateChanged', {
          detail: { type: 'portalTransitionCancelled' },
        })
      );
    }
    this.isTransitioning = value;
  }

  public getPendingQueue(): ReadonlyArray<QueuedIntent> {
    return this.inputQueue.getPendingQueue();
  }

  public getServerQueue(): ReadonlyArray<QueuedIntent> {
    return this.inputQueue.getServerQueue();
  }

  public getQueueCursor(): TilePosition {
    return this.inputQueue.getQueueCursor();
  }

  public getTick(): number {
    return this.tickCounter;
  }

  public claimNextTickTarget(): number {
    const minimumTarget = this.tickCounter + this.getPendingServerIntentCount() + 1;
    const tickTarget = Math.max(this.nextTickTarget, minimumTarget);
    this.nextTickTarget = tickTarget + 1;
    return tickTarget;
  }

  public setServerTick(tick: number): void {
    if (tick < 0) {
      throw new Error(`[GameState] Server tick cannot be negative: ${tick}`);
    }
    const currentTick = this.tickCounter;
    if (tick < currentTick && currentTick > 0) {
      console.warn(`[GameState] Server tick went backwards: ${currentTick} -> ${tick} (possible reconnect)`);
    }
    this.tickCounter = tick;
    this.rebaseNextTickTarget();
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'tickUpdated', tick },
      })
    );
  }

  public enqueueDestination(tile: TilePosition, intentId?: string): boolean {
    const queueFront = this.inputQueue.getQueueFrontPosition();
    const grid = this.worldState.getGrid();
    const direction = grid.snapFacing(
      calculateTargetAngleFromTileCenters(
        grid,
        { x: queueFront.x, y: queueFront.y },
        tile
      )
    );
    const accepted = this.inputQueue.enqueueDestination(tile, {
      map: this.worldState.getMap(),
      direction,
      distance: (x0, y0, x1, y1) => grid.distance(x0, y0, x1, y1),
      onRejected: (reason) => {
        this.dispatchEvent(
          new CustomEvent<StateChangeDetail>('stateChanged', {
            detail: { type: 'pendingMoveRejected', position: tile, reason },
          })
        );
      },
    }, intentId);
    if (accepted) {
      this.emitQueueUpdate();
    }
    return accepted;
  }

  public clearPendingQueue(): void {
    this.inputQueue.clearPendingQueue();
    this.rebaseNextTickTarget();
    this.emitQueueUpdate();
  }

  public removeOneLocalPendingIntent(intent: QueuedIntent): boolean {
    const removed = this.inputQueue.removeOneLocalIntent(intent);
    if (removed) {
      this.emitQueueUpdate();
    }
    return removed;
  }

  public advanceTick(): void {
    this.tickCounter += 1;
  }

  public peekNextIntent(): QueuedIntent | null {
    const queue = this.getPendingQueue();
    return queue.length > 0 ? queue[0] : null;
  }

  public updateServerQueue(queue: QueuedIntent[]): void {
    this.inputQueue.updateServerQueue(queue);
    this.rebaseNextTickTarget();
    this.emitQueueUpdate();
  }

  public applyAuthoritativeState(position: TilePosition, rotation: number): void {
    if (typeof position.x !== 'number' || typeof position.y !== 'number') {
      throw new Error('[GameState] Position must have numeric x,y coordinates');
    }

    if (this.isTransitioning) {
      this.pendingAuthoritativePosition = position;
      this.pendingAuthoritativeRotation = rotation;
      return;
    }

    this.commitAuthoritativeState(position, rotation);
  }

  public completeMapTransition(): void {
    this.isTransitioning = false;
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'mapTransitionComplete', mapId: this.getCurrentMapId() },
      })
    );

    if (this.pendingAuthoritativePosition !== null && this.pendingAuthoritativeRotation !== null) {
      const pendingPos = this.pendingAuthoritativePosition;
      const pendingRotation = this.pendingAuthoritativeRotation;
      this.pendingAuthoritativePosition = null;
      this.pendingAuthoritativeRotation = null;
      this.applyAuthoritativeState(pendingPos, pendingRotation);
    }
  }

  public updateRemotePlayers(players: RemotePlayer[]): void {
    this.entities.updateRemotePlayers(players);
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'remotePlayersUpdated', players },
      })
    );
  }

  public getRemotePlayers(): RemotePlayer[] {
    return this.entities.getRemotePlayers();
  }

  public setMapObjects(objects: readonly import('./types').MapObjectPlacement[]): void {
    this.worldState.setMapObjects(objects);
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'mapObjectsUpdated', objects: [...objects] },
      })
    );
  }

  public addMapObject(
    assetId: string,
    x: number,
    y: number,
    layer = 'objects',
    offsetX = 0,
    offsetY = 0,
    sizeMultiplier = 1,
    flipHorizontal = false,
    flipVertical = false,
    rotationSteps = 0,
  ): import('./types').MapObjectPlacement {
    const placed = {
      id: crypto.randomUUID(),
      asset: assetId,
      x,
      y,
      offsetX,
      offsetY,
      sizeMultiplier,
      flipHorizontal,
      flipVertical,
      rotationSteps,
      layer,
    };
    this.setMapObjects([...this.getMap().objects, placed]);
    return placed;
  }

  public removeMapObject(objectId: string): boolean {
    const current = this.getMap().objects;
    const next = current.filter((object) => object.id !== objectId);
    if (next.length === current.length) {
      return false;
    }
    this.setMapObjects(next);
    return true;
  }

  public updateVisualAssets(assets: VisualAssetPlacement[]): void {
    this.entities.updateVisualAssets(assets);
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'visualAssetsUpdated', assets },
      })
    );
  }

  public getVisualAssets(): VisualAssetPlacement[] {
    return this.entities.getVisualAssets();
  }

  public updateNpcs(npcs: Npc[]): void {
    this.entities.updateNpcs(npcs);
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'npcsUpdated', npcs },
      })
    );
  }

  public getNpcs(): Npc[] {
    return this.entities.getNpcs();
  }

  public updateRuntimeMobs(mobs: RuntimeMob[]): void {
    this.entities.updateRuntimeMobs(mobs);
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'runtimeMobsUpdated', mobs },
      })
    );
  }

  public getRuntimeMobs(): RuntimeMob[] {
    return this.entities.getRuntimeMobs();
  }

  public updateNpcRuntime(npcs: NpcRuntimeData[]): void {
    this.entities.updateNpcRuntime(npcs);
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'npcRuntimeUpdated', npcs },
      })
    );
  }

  public getNpcRuntime(): NpcRuntimeData[] {
    return this.entities.getNpcRuntime();
  }

  public getNpcAt(x: number, y: number): NpcRuntimeData | undefined {
    return this.entities.getNpcRuntime().find(n => n.x === x && n.y === y);
  }

  public updateChestInventory(rows: ChestInventoryRow[]): void {
    this.entities.updateChestInventory(rows);
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'chestInventoryUpdated', rows },
      })
    );
  }

  public getChestInventory(): ChestInventoryRow[] {
    return this.entities.getChestInventory();
  }

  public markEntityMoveSnap(entityId: string, position: TilePosition): void {
    this.entities.markEntityMoveSnap(entityId, position);
  }

  public consumeEntityMoveSnap(entityId: string): TilePosition | null {
    return this.entities.consumeEntityMoveSnap(entityId);
  }

  public clearMapState(): void {
    this.updateRemotePlayers([]);
    this.updateVisualAssets([]);
    this.updateNpcs([]);
    this.updateRuntimeMobs([]);
    this.updateNpcRuntime([]);
    this.updateChestInventory([]);
    this.updateServerQueue([]);
    this.clearPendingQueue();
  }

  private emitQueueUpdate(): void {
    this.resetNextTickTargetIfQueueEmpty();
    this.dispatchEvent(
      new CustomEvent<StateChangeDetail>('stateChanged', {
        detail: { type: 'pendingQueueUpdated', queue: [...this.getPendingQueue()] },
      })
    );
  }

  private commitAuthoritativeState(position: TilePosition, rotation: number): void {
    const commit = this.inputQueue.commitAuthoritativeState(position, rotation, this.worldState.getMap());
    this.emitQueueUpdate();
    if (commit.changed) {
      this.dispatchEvent(
        new CustomEvent<StateChangeDetail>('stateChanged', {
          detail: {
            type: 'characterMoved',
            position: commit.position,
            rotation: commit.rotation,
            tick: this.tickCounter,
          },
        })
      );
    }
  }

  private positionsEqual(a: TilePosition, b: TilePosition): boolean {
    return a.x === b.x && a.y === b.y;
  }

  private rebaseNextTickTarget(): void {
    const minimumTarget = this.tickCounter + this.getPendingServerIntentCount() + 1;
    if (this.nextTickTarget < minimumTarget) {
      this.nextTickTarget = minimumTarget;
    }
  }

  private resetNextTickTargetIfQueueEmpty(): void {
    if (this.getPendingQueue().length === 0) {
      this.nextTickTarget = this.tickCounter + 1;
    }
  }

  private getPendingServerIntentCount(): number {
    const serverQueue = this.inputQueue.getServerQueue();
    let pendingCount = 0;
    for (const intent of serverQueue) {
      if (!intent.validationStatus || intent.validationStatus === 'pending') {
        pendingCount += 1;
      }
    }
    return pendingCount;
  }
}

export function buildDemoMap(width = 10, height = 8): MapDefinition {
  const tiles: TileData[][] = [];
  for (let y = 0; y < height; y += 1) {
    const row: TileData[] = [];
    for (let x = 0; x < width; x += 1) {
      row.push({ x, y, terrain: 'floor', walkable: true });
    }
    tiles.push(row);
  }

  return {
    width,
    height,
    mapKind: 'overworld',
    requireFacingForMove: false,
    tiles,
    portals: [],
    objects: [],
    spawnPoints: { default: { x: Math.floor(width / 2), y: Math.floor(height / 2) } },
    gridType: 'hex',
    pathLayers: [],
    pathBorderContinuations: [],
  };
}
